import { lazy, Suspense, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTrackedAddress } from '../hooks/useTrackedAddress';
import { useWalletSummary } from '../hooks/useWalletSummary';
import { useWalletHistory } from '../hooks/useWalletHistory';
import { useMarketSignal } from '../hooks/useMarketSignal';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { WalletSummaryCard } from '../components/WalletSummaryCard';
import { RefreshStatus } from '../components/RefreshStatus';
import { InstallTip } from '../components/InstallTip';
import { OrientationStrip } from '../components/OrientationStrip';
import { ActivityHistogram } from '../components/ActivityHistogram';
import { TransferList } from '../components/TransferList';
import { SignalCard } from '../components/SignalCard';
import { Skeleton } from '../components/Skeleton';
import { bucketTransfersByTime, isTransferInBucket } from '../lib/activityBuckets';
import { ApiClientError } from '../lib/apiClient';
import { alignSignalPrice } from '../lib/chartSeries';
import styles from './DashboardPage.module.css';

const PriceChart = lazy(() => import('../components/PriceChart'));

// Loads enough recent transfers for the activity histogram to be a
// meaningful scan aid, not just the handful shown by the old fixed limit.
const DASHBOARD_HISTORY_LIMIT = 25;
// Rows listed under the histogram until a bar is chosen; the full list is
// one tap away on the History page.
const DASHBOARD_ROWS = 5;

export function DashboardPage() {
  const { tracked } = useTrackedAddress();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const [selectedBucketIndex, setSelectedBucketIndex] = useState<number | null>(null);

  const summaryQuery = useWalletSummary(tracked.address, tracked.label);
  const historyQuery = useWalletHistory(tracked.address, DASHBOARD_HISTORY_LIMIT);
  const signalQuery = useMarketSignal();

  const loadedTransfers = useMemo(
    () => historyQuery.data?.pages[0]?.transfers ?? [],
    [historyQuery.data],
  );
  const buckets = useMemo(
    () => bucketTransfersByTime(loadedTransfers),
    [loadedTransfers],
  );
  const selectedBucket =
    selectedBucketIndex !== null ? buckets[selectedBucketIndex] : null;

  const visibleTransfers = useMemo(() => {
    if (!selectedBucket) return loadedTransfers;
    // Pending transfers have no timestamp and can't be placed on the
    // timeline, so a time-range selection never hides them.
    return loadedTransfers.filter(
      (transfer) =>
        transfer.confirmationState === 'pending' ||
        isTransferInBucket(transfer, selectedBucket),
    );
  }, [loadedTransfers, selectedBucket]);

  // One current BTC price on the page: the chart and the signal card end on
  // the same reading the summary card shows.
  const rawSignal = signalQuery.data;
  const spotPrice = summaryQuery.data?.btcUsdPrice;
  const signal = useMemo(
    () => (rawSignal ? alignSignalPrice(rawSignal, spotPrice) : undefined),
    [rawSignal, spotPrice],
  );

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['wallet-summary', tracked.address] });
    queryClient.invalidateQueries({ queryKey: ['wallet-history', tracked.address] });
    queryClient.invalidateQueries({ queryKey: ['market-signal'] });
  };

  const summaryErrorMessage =
    summaryQuery.error instanceof ApiClientError ? summaryQuery.error.message : undefined;

  return (
    <div className={styles.page}>
      {!online && (
        <div className={styles.offlineBanner} role="status">
          You are offline. Showing the last cached data.
        </div>
      )}

      <div className={styles.toolbar}>
        <h1 className={styles.heading}>Wallet</h1>
        <RefreshStatus
          updatedAt={summaryQuery.dataUpdatedAt}
          isFetching={summaryQuery.isFetching}
          isError={summaryQuery.isError}
          onRefresh={handleRefresh}
        />
      </div>

      <WalletSummaryCard
        summary={summaryQuery.data}
        previousSummary={summaryQuery.previousSummary}
        isLoading={summaryQuery.isLoading}
        isError={summaryQuery.isError}
        errorMessage={summaryErrorMessage}
        staleSummary={summaryQuery.previousSummary}
      />

      <OrientationStrip label={tracked.label} />

      <section className={styles.chartCard} aria-labelledby="chart-heading">
        <h2 id="chart-heading" className={styles.sectionHeading}>
          90-day price
        </h2>
        {signalQuery.isLoading ? (
          <Skeleton width="100%" height="18rem" />
        ) : signal && signal.series.length > 0 ? (
          <Suspense fallback={<Skeleton width="100%" height="18rem" />}>
            <PriceChart series={signal.series} />
          </Suspense>
        ) : (
          <p className={styles.error}>Chart data is temporarily unavailable.</p>
        )}
      </section>

      <InstallTip />

      <div className={styles.columns}>
        <section
          className={styles.transfersColumn}
          aria-labelledby="recent-transfers-heading"
        >
          <h2 id="recent-transfers-heading" className={styles.sectionHeading}>
            Activity
          </h2>
          {historyQuery.isLoading ? (
            <Skeleton width="100%" height="29rem" />
          ) : historyQuery.isError ? (
            <p className={styles.error} role="alert">
              Recent transfer history is temporarily unavailable.
            </p>
          ) : (
            <>
              <ActivityHistogram
                transfers={loadedTransfers}
                selectedBucketIndex={selectedBucketIndex}
                onSelectBucket={setSelectedBucketIndex}
              />
              <TransferList
                transfers={visibleTransfers}
                compactAmounts
                // A chosen bar lists all of its transfers.
                limit={selectedBucket ? undefined : DASHBOARD_ROWS}
              />
              <Link to="/history" className={styles.seeAll}>
                See all transfers
              </Link>
            </>
          )}
        </section>

        <div className={styles.signalColumn}>
          <SignalCard
            signal={signal}
            isLoading={signalQuery.isLoading}
            isError={signalQuery.isError}
          />
        </div>
      </div>
    </div>
  );
}
